import type {
  LockedView,
  UnlockPreviewResponse,
  ViewDTO,
  ViewGroup,
  ViewUpsertDTO,
} from '@tempo-io/roadmap-dto';
import { isLocked, parseViewDTO, unlockPreviewResponseSchema } from '@tempo-io/roadmap-dto';
import { tempoApi } from '../tempoApi';

export type { UnlockPreviewItem, UnlockPreviewResponse } from '@tempo-io/roadmap-dto';

const fetchViews = async (loopId: number, viewGroup?: ViewGroup): Promise<ViewDTO[]> => {
  const response = await tempoApi().get(`api/v1/loop-visualization/${loopId}/view`, {
    searchParams: viewGroup ? { view_group: viewGroup } : undefined,
  });
  const data = await response.json<unknown[]>();
  return data.map(parseViewDTO);
};

const fetchView = async (id: number): Promise<ViewDTO> => {
  const response = await tempoApi().get(`api/v1/loop-visualization/view/${id}`);
  return parseViewDTO(await response.json());
};

const upsertView = async (id: number | undefined, data: ViewUpsertDTO): Promise<ViewDTO> => {
  const url = id ? `api/v1/loop-visualization/view/${id}` : `api/v1/loop-visualization/view`;
  const response = await tempoApi().put(url, { json: data });
  return parseViewDTO(await response.json());
};

const deleteView = async (id: number): Promise<void> => {
  await tempoApi().delete(`api/v1/loop-visualization/view/${id}`);
};

const duplicateView = async (id: number): Promise<ViewDTO> => {
  const response = await tempoApi().post(`api/v1/loop-visualization/view/${id}/duplicate`);
  return parseViewDTO(await response.json());
};

const lockView = async (id: number): Promise<LockedView> => {
  const response = await tempoApi().post(`api/v1/loop-visualization/view/${id}/lock`);
  const parsed = parseViewDTO(await response.json());
  if (!isLocked(parsed)) {
    throw new Error('Lock view API returned an unlocked view');
  }
  return parsed;
};

const fetchUnlockPreview = async (id: number): Promise<UnlockPreviewResponse> => {
  const response = await tempoApi().get(`api/v1/loop-visualization/view/${id}/lock`);
  return unlockPreviewResponseSchema.parse(await response.json());
};

const unlockView = async (id: number): Promise<ViewDTO> => {
  const response = await tempoApi().delete(`api/v1/loop-visualization/view/${id}/lock`);
  return parseViewDTO(await response.json());
};

export const ViewApi = {
  fetchViews,
  fetchView,
  upsertView,
  deleteView,
  duplicateView,
  lockView,
  fetchUnlockPreview,
  unlockView,
} as const;
